<template>
  <div
    ref="elementRef"
    class="parent relative"
    :class="[
      parentHoverClasses,
      {
        'overflow-hidden': parallaxLayer,
      },
    ]"
  >
    <div
      class="relative"
      :class="[
        childHoverClasses,
        {
          'h-full': fullHeight,
          'overflow-hidden': parallaxOffset,
        },
      ]"
    >
      <base-picture
        v-if="badge"
        :alt="badge.alt"
        class="absolute right-0 top-0 m-4 h-14 w-14"
        fit="contain"
        :src="badge.url"
      />
      <div
        :class="{ 'h-full': fullHeight }"
        :style="parallaxOffset ? `transform: scale(${parallaxScale})` : null"
      >
        <component
          :is="parallaxOffset ? LazyBaseParallax : 'div'"
          :class="{ 'h-full': fullHeight }"
          :speed="parallaxSpeed"
        >
          <base-picture
            v-if="responsiveMedia.images"
            v-bind="responsiveMedia.images"
            class="w-full "
            :class="{ 'h-full': fullHeight }"
            fit="cover"
            :lazy
          />
          <vf-video
            v-else-if="responsiveMedia.video"
            v-bind="responsiveMedia.video"
            ref="videoRef"
            :autoplay="!lazy"
            class="w-full"
            :class="{ 'h-full': fullHeight }"
            :controls
            fit="cover"
            loop
            muted
          />
        </component>
      </div>
      <cms-base-gradient
        v-if="gradientStyle && gradientStyle !== 'off'"
        v-bind="{ gradientTone, gradientStyle, gradientBreakpoints }"
        :alignment="gradientAlignment"
        :position="gradientPosition"
      />
      <base-link
        v-if="mediaLink"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
    </div>
    <cms-base-caption v-if="hasCaption" v-bind="{ captionColor, captionStyle, captionPosition, captionText }" />
    <component
      :is="parallaxLayer ? LazyBaseParallax : 'div'"
      v-bind="parallaxLayer"
      class="w-full"
      :class="{ 'absolute-0': parallaxLayer }"
    >
      <div
        v-style:bg="backgrounds"
        class="w-full"
        :class="[contentLayoutClasses, contentAlignmentClasses, brandClasses.container, overlayAlignmentClasses]"
      >
        <div v-if="eyebrowText" v-style:c="eyebrowTextColor || subtitleColor" class="uppercase">
          {{ eyebrowText }}
        </div>

        <component
          :is="titleTag"
          v-if="title"
          v-style:c="titleColor"
          :class="[titleStyle, { 'sr-only': hideTitle }]"
          data-test-id="cms-linked-card-title"
        >
          <base-link
            v-if="mediaLink"
            :data-cta="mediaLink.delocalizedTracking"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('title')"
          >
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </base-link>
          <template v-else>
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </template>
        </component>
        <p
          v-if="subtitle"
          v-style:c="subtitleColor"
          :class="subtitleStyle"
          data-test-id="cms-linked-card-text"
        >
          <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
          <span v-else>{{ subtitle }}</span>
        </p>
        <cms-rich-text
          v-if="richText"
          v-style:c="richTextColor || subtitleColor"
          class="pointer-within"
          :content="{ richText }"
        />
        <div
          v-if="linkType !== 'No-CTA' && targets.length"
          :class="[ctaAlignmentClasses, equalTargetSizeClass, brandClasses.ctaContainer]"
          :style="{ '--cols': cols }"
        >
          <cms-shared-button
            v-for="(target, i) in targets"
            :key="i"
            v-bind="{ ...target, ...getTextLinkStyles(content) }"
            :size="sharedButton.size"
            @click="handlePromotionClick(`cta${i + 1}`)"
          />
        </div>
      </div>
    </component>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type { LinkedCardAlignment, LinkedCardContent, LinkedCardLayout } from '#types/components/cms/linked-card'
import type { Target } from '#types/target'
import { LazyBaseParallax } from '#components'
import { ParallaxLayerKey } from '#content/contexts/parallax'
import { SectionContextKey } from '#content/contexts/section'

const {
  content,
  controls = true,
  index,
  isPartOfCollection,
  firstElement,
  parentHeadingLevel
} = defineProps<{
  content: LinkedCardContent
  controls?: boolean
  firstElement?: boolean
  fullHeight?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
}>()

const {
  backdrop,
  backgroundColor,
  badge,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowText,
  eyebrowTextColor,
  gradientBreakpoints,
  gradientStyle,
  gradientTone,
  hideTitle,
  linkType,
  media,
  mediaTarget,
  name,
  overlayWidth,
  parallax,
  promotionTracking,
  responsiveLayout,
  richText,
  richTextColor,
  subtitle,
  subtitleHtml,
  subtitleColor,
  subtitleStyle,
  targets = [] as Target[],
  title,
  titleHtml,
  titleColor,
  titleStyle,
  captionColor = subtitleColor,
} = content

const appConfig = useAppConfig()
const { linkedCard: { brandClasses }, sharedButton } = appConfig.components.cms
const { breakpoints } = appConfig.ds
const { getMedia } = useCms()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)
const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const parallaxLayer = inject(ParallaxLayerKey)
const videoRef = ref()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getMedia(media)

const parallaxScaleMap = {
  0: '1',
  1: '1.05',
  2: '1.1',
  3: '1.15',
  4: '1.2',
  5: '1.25',
}

const parallaxOffsetMap = {
  0: 0,
  1: 5,
  2: 10,
  3: 13,
  4: 16,
  5: 20,
}

const getParallaxSpeed = (offset: number | undefined): number | null => {
  if (!offset || offset === 0) return null
  const speed = parallaxOffsetMap[Math.abs(offset)]
  return offset > 0 ? speed : -speed
}

const parallaxOffset = parallax?.config.innerOffset
const parallaxSpeed = getParallaxSpeed(parallaxOffset)
const parallaxScale = parallaxOffset ? parallaxScaleMap[Math.abs(parallaxOffset)] : null

const contentLayout: Partial<Record<LinkedCardLayout, Responsive<string>>> = {
  'overlay': { sm: '<md:absolute <md:bottom-0 <md:left-0 <md:mb-4', md: 'md:absolute md:bottom-0 md:left-0 md:mb-4' },
  'overlay-bottom': { sm: '<md:absolute <md:bottom-0 <md:left-0 <md:mb-4', md: 'md:absolute md:bottom-0 md:left-0 md:mb-4' },
  'overlay-top': { sm: '<md:absolute <md:top-0 <md:left-0 <md:mt-4', md: 'md:absolute md:top-0 md:left-0 md:mt-4' },
  'overlay-middle': { sm: '<md:absolute <md:top-1/2 <md:-translate-y-1/2 <md:mt-4', md: 'md:absolute md:top-1/2 md:-translate-y-1/2 md:mt-4' }
}

const contentAlignment: Partial<Record<LinkedCardAlignment, Responsive<string>>> = {
  left: { sm: '<md:justify-start <md:text-left', md: 'md:justify-start md:text-left' },
  center: { sm: '<md:items-center <md:text-center', md: 'md:items-center md:text-center' },
  right: { sm: '<md:items-end <md:text-right', md: 'md:items-end md:text-center' }
}

const ctaAlignmentSmall: Record<LinkedCardAlignment, Responsive<string>> = {
  left: { sm: '<md:items-start', md: 'md:items-start' },
  center: { sm: '<md:items-center', md: 'md:items-center' },
  right: { sm: '<md:items-end', md: 'md:items-end' },
}

const ctaAlignmentMedium: Record<LinkedCardAlignment, Responsive<string>> = {
  left: { sm: '<md:items-start', md: 'md:items-start' },
  center: { sm: '<md:justify-center', md: 'md:justify-center' },
  right: { sm: '<md:justify-end', md: 'md:justify-end' },
}

const contentLayoutClasses = [
  contentLayout[responsiveLayout.sm.layout]?.sm,
  responsiveLayout.md?.layout && contentLayout[responsiveLayout.md.layout]?.md,
].filter(Boolean).join(' ')

const contentAlignmentClasses = [
  contentAlignment[responsiveLayout.sm.alignment]?.sm,
  responsiveLayout.md?.alignment && contentAlignment[responsiveLayout.md.alignment]?.md,
].filter(Boolean).join(' ')

const ctaAlignmentClasses = [
  ctaAlignmentSmall[responsiveLayout.sm.alignment]?.sm,
  responsiveLayout.md?.alignment && ctaAlignmentMedium[responsiveLayout.md.alignment]?.md,
].filter(Boolean).join(' ')

const gradientDirection = {
  'overlay-top': 'top',
  'overlay-bottom': 'bottom',
  'overlay-middle': 'center'
}

const gradientPosition = {
  sm: gradientDirection[responsiveLayout.sm.layout],
  md: responsiveLayout.md?.layout && gradientDirection[responsiveLayout.md.layout],
}

const gradientAlignment = {
  sm: responsiveLayout.sm.alignment,
  md: responsiveLayout.md?.alignment
}

const isOverlayLayout: Responsive<boolean | undefined> = {
  sm: getValueForBreakpoint('sm', responsiveLayout)?.layout?.includes('overlay'),
  md: getValueForBreakpoint('md', responsiveLayout)?.layout?.includes('overlay'),
  lg: getValueForBreakpoint('lg', responsiveLayout)?.layout?.includes('overlay')
}

const parentHoverClasses = {
        '<md:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.sm,
  '~md:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.md,
  'lg:overflow-hidden': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.lg,
}

const childHoverClasses = {
        '<md:duration <md:parent-hover:rotate-2 <md:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.sm,
  '~md:duration ~md:parent-hover:rotate-2 ~md:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.md,
  'lg:duration lg:parent-hover:rotate-2 lg:parent-hover:scale-105': !responsiveMedia.video && !parallaxOffset && mediaLink && isOverlayLayout.lg,
}

const equalTargetSizeClass = {
  '<md:grid <md:cols-$cols <md:auto-rows-fr': getValueForBreakpoint('sm', equalTargetSize),
  '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch': getValueForBreakpoint('md', equalTargetSize),
  'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch': getValueForBreakpoint('lg', equalTargetSize),
}

const cols = isPartOfCollection
  ? 'repeat(1,minmax(0,1fr))'
  : targets.map((target) => target.style?.sm === 'text-link' ? 'max-content' : 'minmax(0,1fr)').join(' ')

const backgrounds = Object.keys(breakpoints || {}).reduce((acc, cur) => {
  const layout = getValueForBreakpoint(cur as keyof Responsive, responsiveLayout)?.layout

  return { ...acc, [cur]: layout?.includes('overlay') ? backdrop?.[cur] : backgroundColor?.[cur] }
}, {} as Record<keyof Responsive, string>)

const titleTag = isPartOfCollection
  ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
  : firstElement ? 'h1' : 'h2'

const overlaySize = {
  sm: {
    xs: '<md:w-10/12',
    sm: '<md:w-10/12',
    md: '<md:w-10/12',
    lg: '<md:w-full'
  },
  md: {
    xs: 'md:w-1/3',
    sm: 'md:w-1/2',
    md: 'md:w-2/3',
    lg: 'md:w-11/12'
  }
}

const overlayAlignment = {
  sm: '<md:left-1/2 <md:-translate-x-1/2',
  md: 'md:left-1/2 md:-translate-x-1/2'
}

const overlayAlignmentClasses = overlayWidth && ['sm', 'md']
  .map((bp) => responsiveLayout[bp].layout.includes('overlay') && [overlaySize[bp][overlayWidth], responsiveLayout[bp].alignment === 'center' && overlayAlignment[bp]])

const hasCaption = captionText && captionPosition !== 'off'

onMounted(() => videoRef.value?.play().catch((e) => log.error(e)))
</script>
